/**
 * CertificatesPage -- CRUD for certificates in the content library.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useContentStore, useContentActions } from '../../state/cms/useContentStore';
import { Panel } from '../../components/ui/Panel';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { MonthYearPicker } from '../../components/ui/MonthYearPicker';
import { Button } from '../../components/ui/Button';
import { LoadingState } from '../../components/ui/LoadingState';
import { useSaveHandler } from '../../hooks/useSaveHandler';
import { logger } from '../../services/logger.client';
import type { Certificate } from '../../types/cms.types';

const log = logger.child({ module: 'CertificatesPage' });

/* ------------------------------------------------------------------ */
/*  Certificate Editor                                                */
/* ------------------------------------------------------------------ */

interface EditorProps {
  entry: Certificate;
}

function CertificateEditor({ entry }: EditorProps) {
  const { updateCertificate, deleteCertificate } = useContentActions();

  const [name, setName] = useState(entry.name);
  const [org, setOrg] = useState(entry.issuing_organization);
  const [issueDate, setIssueDate] = useState(entry.issue_date ?? '');
  const [expiryDate, setExpiryDate] = useState(entry.expiry_date ?? '');
  const [credId, setCredId] = useState(entry.credential_id ?? '');
  const [credUrl, setCredUrl] = useState(entry.credential_url ?? '');
  const [description, setDescription] = useState(entry.base_description ?? '');
  const { isSaving, saveMessage, saveError, execute: executeSave } = useSaveHandler({
    getStoreError: () => useContentStore.getState().error,
  });

  async function handleSave() {
    await executeSave(() =>
      updateCertificate(entry.id, {
        name,
        issuing_organization: org,
        issue_date: issueDate || null,
        expiry_date: expiryDate || null,
        credential_id: credId || null,
        credential_url: credUrl || null,
        base_description: description || null,
      }),
    );
  }

  async function handleDelete() {
    if (!window.confirm('Delete this certificate?')) return;
    await deleteCertificate(entry.id);
  }

  return (
    <Panel>
      <Panel.Header>{entry.name}</Panel.Header>
      <Panel.Content>
        <div className="editor-field-group">
          <div className="experience-entry__row">
            <Input
              label="Certificate Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Input
              label="Issuing Organization"
              value={org}
              onChange={(e) => setOrg(e.target.value)}
            />
          </div>
          <div className="experience-entry__row">
            <MonthYearPicker
              label="Issue Date"
              value={issueDate}
              onChange={(v) => {
                setIssueDate(v);
                if (expiryDate && v && expiryDate < v) setExpiryDate('');
              }}
              placeholder="Select issue month"
            />
            <MonthYearPicker
              label="Expiry Date"
              value={expiryDate}
              onChange={setExpiryDate}
              placeholder="Select expiry month"
              minValue={issueDate || undefined}
            />
          </div>
          <div className="experience-entry__row">
            <Input
              label="Credential ID"
              value={credId}
              onChange={(e) => setCredId(e.target.value)}
              placeholder="ABC-12345"
            />
            <Input
              label="Credential URL"
              value={credUrl}
              onChange={(e) => setCredUrl(e.target.value)}
              placeholder="https://..."
            />
          </div>
          <Textarea
            label="Description"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Additional details about the certification..."
          />
          <div className="cms-page__actions-row">
            {saveError && (
              <span className="editor-page__save-msg editor-page__save-msg--error">
                {saveError}
              </span>
            )}
            {saveMessage && (
              <span className="editor-page__save-msg">{saveMessage}</span>
            )}
            <Button variant="danger" size="sm" onClick={() => void handleDelete()}>
              Delete
            </Button>
            <Button variant="primary" size="sm" onClick={() => void handleSave()} loading={isSaving}>
              Save
            </Button>
          </div>
        </div>
      </Panel.Content>
    </Panel>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                              */
/* ------------------------------------------------------------------ */

export function CertificatesPage() {
  const certificates = useContentStore((s) => s.certificates);
  const isLoading = useContentStore((s) => s.isLoading);
  const error = useContentStore((s) => s.error);
  const { fetchCertificates, createCertificate } = useContentActions();

  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newOrg, setNewOrg] = useState('');

  useEffect(() => {
    log.info('CertificatesPage mounted');
    void fetchCertificates();
  }, [fetchCertificates]);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (!newName.trim() || !newOrg.trim()) return;

    await createCertificate({
      name: newName.trim(),
      issuing_organization: newOrg.trim(),
      display_order: certificates.length,
    });

    setNewName('');
    setNewOrg('');
    setShowCreate(false);
  }

  return (
    <div className="cms-page">
      <header className="cms-page__header">
        <h1 className="cms-page__title">Certificates</h1>
        <Button
          variant="primary"
          size="md"
          onClick={() => setShowCreate(!showCreate)}
        >
          + Add Certificate
        </Button>
      </header>

      {showCreate && (
        <Panel>
          <Panel.Header>New Certificate</Panel.Header>
          <Panel.Content>
            <form className="editor-field-group" onSubmit={handleCreate} noValidate>
              <div className="experience-entry__row">
                <Input
                  label="Certificate Name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="AWS Solutions Architect"
                  required
                />
                <Input
                  label="Issuing Organization"
                  value={newOrg}
                  onChange={(e) => setNewOrg(e.target.value)}
                  placeholder="Amazon Web Services"
                  required
                />
              </div>
              <div className="cms-page__actions-row">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowCreate(false)}
                  type="button"
                >
                  Cancel
                </Button>
                <Button variant="primary" size="sm" type="submit">
                  Create
                </Button>
              </div>
            </form>
          </Panel.Content>
        </Panel>
      )}

      <LoadingState loading={isLoading} error={error}>
        {certificates.length === 0 ? (
          <div className="list-empty">
            No certificates yet. Click '+ Add Certificate' to get started.
          </div>
        ) : (
          <div className="cms-page__body">
            {certificates.map((cert) => (
              <CertificateEditor key={cert.id} entry={cert} />
            ))}
          </div>
        )}
      </LoadingState>
    </div>
  );
}
